<template>
  <el-dialog :visible="visible" :title="title" custom-class="with-title" @close="hide">
    <div v-loading="loading">
      <div class="dialog-content">
        <div class="message message-warning">
          1人の応募者につき、応募グループは1件までです。<br />複数の応募グループに対して応募があった場合は、最初の応募が優先されます。
        </div>
        <!-- 基本情報-->
        <div class="caption">基本情報</div>
        <!-- 応募グループ名-->
        <div class="setting-content">
          <Panel>
            <div class="input-container">
              <div class="label">応募グループ名<span class="badge badge-danger instagram ml-x1">必須</span></div>
              <div class="input">
                <el-input
                  placeholder="あたり:キーワードA, はずれ:キーワードB など"
                  :maxlength="120"
                  :class="{ success: campaignPrize.name.length > 0 }"
                  type="text"
                  v-model="campaignPrize.name"
                  tabindex="1"
                ></el-input>
              </div>
            </div>
          </Panel>
        </div>
        <!-- 応募キーワード-->
        <div class="setting-content">
          <Panel>
            <div class="input-container">
              <div class="label">
                応募キーワード
                <el-tooltip
                  placement="top"
                  content="この応募グループへの応募を判定するキーワードを入力してください。複数の応募グループに同じキーワードがセットされている場合、ランダムで振り分けられます。"
                  ><i class="mdi mdi-help-circle ml-x1"></i
                ></el-tooltip>
              </div>
              <div class="input keyword-list">
                <div class="label-keyword mr-x1">全て含む</div>
                <tag-input
                  :tags="searchKeyword"
                  :disabled="true"
                  add-tag-text="キーワード追加"
                  v-if="searchKeyword && isShowCampaignHashtag"
                ></tag-input>
                <tag-input
                  :tags="campaignPrize.entryKeywords"
                  :onChange="onChangeEntryKeywords"
                  add-tag-text="キーワード追加"
                  :disabled="isCampaignFinished"
                ></tag-input>
              </div>
            </div>
          </Panel>
        </div>
        <!-- 当選数-->
        <div class="setting-content">
          <Panel>
            <div class="input-container">
              <div class="label">
                当選数
                <el-tooltip
                  placement="top"
                  content="応募に当選者を設定する場合、当選数を入力してください。外れ応募とする場合、0を入力してください。"
                  ><i class="mdi mdi-help-circle ml-x1"></i
                ></el-tooltip>
              </div>
              <el-input-number
                controls-position="right"
                v-model="campaignPrize.maxWinnerCount"
                :min="0"
                :max="99999"
                tabindex="2"
                :step="1"
                @change="onChangeMaxWinnerCount"
              ></el-input-number>
            </div>
          </Panel>
        </div>
      </div>
      <div class="dialog-footer" slot="footer">
        <div v-if="!campaignPrize.id">
          <el-button type="primary" @click="createOrUpdate()" :disabled="!isFormValid || isSubmitting"
            ><i class="mdi mdi-content-save"></i>新規作成</el-button
          >
        </div>
        <div v-if="campaignPrize.id" layout layout-align="space-between">
          <el-button type="default" @click="deleteCampaignPrize()" :disabled="isDeleteDisabled"
            ><i class="mdi mdi-delete-forever"></i>削除</el-button
          >
          <el-button type="primary" @click="createOrUpdate()" :disabled="!isFormValid || isSubmitting"
            ><i class="mdi mdi-content-save"></i>保存</el-button
          >
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.input-container {
  width: 100%;

  .label {
    margin-bottom: $spacing-unit;
    font-weight: bold;
  }

  .mdi-help-circle {
    margin-left: $spacing-unit;
    font-size: 20px;
  }

  .el-input-number {
    width: $spacing-unit * 18;
  }

  .label-keyword {
    font-size: $font-small-size;
  }
}

.setting-content:not(:last-child) {
  margin-bottom: $spacing-unit * 2;
}

.caption {
  margin: $spacing-unit * 4 0 $spacing-unit * 2 0;

  &:first-child {
    margin-top: 0;
  }
}

.keyword-list > div {
  display: inline-block;
}

.badge.badge-danger.instagram {
  position: initial;
  display: inline-block;
  font-weight: 100;
}
</style>
<script lang="ts">
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'

import Panel from '@/client/components/_atoms/Panel.vue'
import TagInput from '@/client/components/_atoms/tag_input'
import { CAMPAIGN_STATUS } from '@/client/constant'
import api from '@/client/core/api'
import { TrackingService } from '@/client/services'
import { useInstagramCampaignPrizeStore } from '@/client/store/useInstagramCampaignPrizeStore'
import { validateKeywords } from '@/client/utils/validation/keyword'

type CampaignPrizeForm = {
  id: number
  campaignId: number
  name: string
  maxWinnerCount: number
  oldMaxWinnerCount: number
  entryKeywords: string[]
}

export default defineComponent({
  props: {
    prizeCount: Number
  },
  components: {
    TagInput,
    Panel
  },
  data() {
    return {
      title: '',
      visible: false,
      loading: false,
      campaignPrize: {
        id: 0,
        campaignId: 0,
        name: '',
        maxWinnerCount: 0,
        oldMaxWinnerCount: 0,
        entryKeywords: []
      } as CampaignPrizeForm,
      rawCampaignPrizeData: {},
      errorEntryKeywordMessage: '' as string | undefined,
      isSubmitting: false,
      isSubmited: false
    }
  },
  computed: {
    ...mapState(useInstagramCampaignPrizeStore, {
      campaign: (state): Partial<NonNullable<typeof state.campaign>> => {
        if (state.campaign) {
          return state.campaign
        }
        return {}
      },
      searchKeyword: state => {
        if (state.campaign && state.campaign.setting) {
          return JSON.parse(state.campaign.setting.searchKeyword)
        }
        return null
      },
      orKeyword: state => {
        if (state.campaign && state.campaign.setting && state.campaign.setting.orKeyword) {
          return JSON.parse(state.campaign.setting.orKeyword)
        }
        return null
      }
    }),

    isFormValid() {
      if (!this.campaignPrize.name) {
        return false
      }

      return true
    },

    isShowCampaignHashtag() {
      return this.campaign.setting && this.campaign.setting.entryActionType !== 'comment'
    },

    isCampaignFinished() {
      const status = this.campaign.status
      return (
        status !== undefined &&
        [CAMPAIGN_STATUS.DURING_FINAL_COLLECTION, CAMPAIGN_STATUS.COLLECTION_COMPLETED].includes(status)
      )
    },

    isDeleteDisabled() {
      return this.isCampaignFinished || this.prizeCount === 1
    },

    isFormChanged(): boolean {
      return JSON.stringify(this.campaignPrizeData) !== JSON.stringify(this.rawCampaignPrizeData)
    },

    campaignPrizeData() {
      return this.setCampaignPrizeData()
    }
  },
  methods: {
    ...mapActions(useInstagramCampaignPrizeStore, ['fetchPrizes', 'fetchCampaign']),

    /**
     * Set campaign prize data
     * @returns {Object} campaign prize data
     */
    setCampaignPrizeData() {
      return {
        campaignId: this.campaignPrize.campaignId,
        name: this.campaignPrize.name,
        maxWinnerCount: this.campaignPrize.maxWinnerCount,
        oldMaxWinnerCount: this.campaignPrize.maxWinnerCount,
        entryKeyword: JSON.stringify(this.campaignPrize.entryKeywords)
      }
    },

    /**
     * Get data from api
     */
    async getCampaignPrize() {
      this.loading = true
      const result = await api.get('/instagram_campaign_prizes/' + this.campaignPrize.id)
      this.loading = false
      const campaignPrize = result.data

      if (!campaignPrize) {
        this.$notify({
          title: '対象の応募グループはすでに削除されています。',
          message: '',
          customClass: 'danger',
          duration: 5000
        })
        this.close()
        return
      }

      this.campaignPrize = {
        id: campaignPrize.id,
        campaignId: campaignPrize.campaignId,
        name: campaignPrize.name,
        maxWinnerCount: campaignPrize.maxWinnerCount,
        oldMaxWinnerCount: campaignPrize.maxWinnerCount,
        entryKeywords: campaignPrize.entryKeyword ? JSON.parse(campaignPrize.entryKeyword) : []
      }
    },

    async createOrUpdate() {
      if (this.campaignPrize.id) {
        TrackingService.sendEvent('click:応募グループ一覧|応募グループの編集(IG)|保存')

        await this.updateCampaignPrize()
      } else {
        await this.createCampaignPrize()
      }
      this.close()
    },

    /**
     * 応募グループの新規作成
     */
    async createCampaignPrize() {
      this.isSubmitting = true
      if (!this.isFormValid) {
        return
      }

      // キャンペーンを保存する
      const result = await api.post('/instagram_campaign_prizes', this.campaignPrizeData)

      setTimeout(() => {
        this.isSubmitting = false
      }, 2000)

      if (result && result.data) {
        // 成功メッセージを表示
        this.$notify({
          title: '応募グループを作成しました。',
          message: '',
          customClass: 'success',
          duration: 5000
        })
        return
      }

      this.$notify({
        title: '応募グループの新規作成に失敗しました。',
        message: '恐れ入りますが、時間をおいて再度お試しください。',
        customClass: 'danger',
        duration: 5000
      })
    },

    /**
     * 応募グループの編集
     */
    async updateCampaignPrize() {
      this.isSubmitting = true
      if (!this.isFormValid) {
        return
      }

      // キャンペーンを保存する
      const result = await api.put('/instagram_campaign_prizes/' + this.campaignPrize.id, this.campaignPrizeData)

      setTimeout(() => {
        this.isSubmitting = false
      }, 2000)

      if (result && result.data) {
        // 成功メッセージを表示
        this.$notify({
          title: '応募グループを保存しました。',
          message: '',
          customClass: 'success',
          duration: 5000
        })

        return
      }

      if (result.error && result.error.type === 'NOT_EXISTS') {
        this.$notify({
          title: '対象の応募グループはすでに削除されています。',
          message: '',
          customClass: 'danger',
          duration: 5000
        })

        return
      }

      this.$notify({
        title: '応募グループの保存に失敗しました。',
        message: '恐れ入りますが、時間をおいて再度お試しください。',
        customClass: 'danger',
        duration: 5000
      })
    },

    /**
     * 応募グループの削除
     */
    async deleteCampaignPrize() {
      TrackingService.sendEvent('click:応募グループ一覧|応募グループの編集(IG)|削除')

      const confirmDelete = confirm('応募グループを削除してよろしいですか？')
      if (!confirmDelete) {
        return
      }

      const result = await api.delete('/instagram_campaign_prizes/' + this.campaignPrize.id)

      if (result && result.data) {
        this.$notify({
          title: '応募グループを削除しました。',
          message: '',
          customClass: 'success',
          duration: 5000
        })
        this.close()
        return
      }

      if (result.error && result.error.type === 'NOT_EXISTS') {
        this.$notify({
          title: '対象の応募グループはすでに削除されています。',
          message: '',
          customClass: 'danger',
          duration: 5000
        })
        this.close()
        return
      }

      this.$notify({
        title: '応募グループの削除に失敗しました。',
        message: '恐れ入りますが、時間をおいて再度お試しください。',
        customClass: 'danger',
        duration: 5000
      })
      this.close()
    },

    /**
     * Execute when max winner count is changed
     * @param {number} maxWinnerCount
     */
    onChangeMaxWinnerCount(maxWinnerCount: number) {
      if (maxWinnerCount === this.campaignPrize.oldMaxWinnerCount + 1) {
        TrackingService.sendEvent('click:応募グループ一覧|応募グループの編集(IG)|当選数|UP')
      } else if (maxWinnerCount === this.campaignPrize.oldMaxWinnerCount - 1) {
        TrackingService.sendEvent('click:応募グループ一覧|応募グループの編集(IG)|当選数|DOWN')
      } else TrackingService.sendEvent('input:応募グループ一覧|応募グループの編集(IG)|当選数')

      this.campaignPrize.oldMaxWinnerCount = this.campaignPrize.maxWinnerCount

      if (!maxWinnerCount) {
        setTimeout(() => {
          this.campaignPrize.maxWinnerCount = 0
        }, 100)
      }
    },

    /**
     * Valid entry keyword
     * @param {string[]} keywords list keyword
     */
    onChangeEntryKeywords(keywords: string[]) {
      const origin = Object.assign([], this.campaignPrize.entryKeywords)

      if (!this.isValidKeyword(keywords, origin)) {
        this.campaignPrize.entryKeywords = origin
        return
      }
      this.errorEntryKeywordMessage = ''
      this.campaignPrize.entryKeywords = keywords
    },

    /**
     * Check if keyword is valid
     * @param {string[]} keywords list keyword
     * @param {string} origin
     * @returns {boolean}
     */
    isValidKeyword(keywords: string[], origin: string[]): boolean {
      const result = validateKeywords(keywords, origin, { mode: 'whitespace_and_length_only' })

      if (!result.isValid) {
        this.errorEntryKeywordMessage = result.errorMessage
        this.$notify({
          title: result.errorMessage ?? '',
          message: '',
          customClass: 'danger',
          duration: 5000
        })
      }

      return result.isValid
    },

    /**
     * ダイアログを開く
     */
    async open(campaignId: number, id: number) {
      this.visible = true
      if (id) {
        this.campaignPrize.id = id
        this.title = '応募グループの編集'
        await this.getCampaignPrize()
      } else {
        this.title = '応募グループの新規作成'
      }

      this.campaignPrize.campaignId = campaignId
      this.rawCampaignPrizeData = this.setCampaignPrizeData()
    },

    /**
     * Close dialog after submit
     * @returns {Promise<void>} void
     */
    async close() {
      this.isSubmited = true
      this.visible = false
      await this.fetchPrizes()
      await this.fetchCampaign()
    },

    /**
     * Close dialog
     * @returns {void}: void
     */
    hide(): void {
      TrackingService.sendEvent('click:応募グループ一覧|応募グループの編集(IG)|閉じる')

      if (this.isFormChanged && !this.isSubmited) {
        const message = '編集内容が保存されていません。ダイアログを閉じてよろしいですか?'
        const confirm = window.confirm(message)

        if (!confirm) {
          return
        }
      }

      this.resetForm()
      this.visible = false
      this.loading = false
      this.isSubmited = false
    },

    resetForm() {
      this.campaignPrize = {
        id: 0,
        campaignId: 0,
        name: '',
        maxWinnerCount: 0,
        oldMaxWinnerCount: 0,
        entryKeywords: []
      }
      this.errorEntryKeywordMessage = ''
    }
  }
})
</script>
